import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress, Button, Popper } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import Box from 'retired/Box';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import Campaign from 'entities/campaign/model';
import { useInjectEpics } from 'utils/injectEpics';
import { gray5, lightGray } from 'utils/color-styles';
import { useInjectReducer } from 'utils/injectReducer';
import { selectCampaigns } from 'entities/campaign/selectors';
import { CampaignFormType } from 'containers/CampaignPage/types';
import CampaignsPageBanner from 'assets/pageBanners/campaigns.avif';
import { selectUser, selectOrganization } from 'entities/user/selectors';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';

import CampaignsTable from './CampaignsTable';
import { initAction } from './types';
import { selectLoading } from './selectors';
import reducer from './reducer';
import epics from './epics';

interface TabItemProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

interface CampaignMenuItem {
  text: string;
  type: CampaignFormType;
}

function TabItem(props: TabItemProps): JSX.Element {
  const { isActive, label, onClick } = props;
  const normal = {
    color: gray5,
    fontFamily: 'inherit',
    fontWeight: 400,
    fontSize: 14,
    marginTop: 15,
    marginBottom: 0,
    marginRight: 25,
    padding: 0,
    paddingBottom: 14,
    cursor: 'pointer',
  };
  const active = {
    ...normal,
    color: '#1E1F20',
    borderBottom: '1px solid #1E1F20',
    fontWeight: 500,
  };

  return (
    <button style={isActive ? active : normal} onClick={onClick} type="button">
      {label}
    </button>
  );
}

const key = 'campaignsPage';

const CampaignCreationDropDown = ({
  anchorEl,
  data,
  handleClick,
}: {
  anchorEl: any;
  data: CampaignMenuItem[];
  handleClick: (event: any) => void;
}): JSX.Element | null => (
  <>
    <Button
      color="primary"
      variant="outlined"
      size="large"
      onClick={handleClick}
      style={{ lineHeight: '24px', width: '200px' }}
    >
      Create Campaign <ArrowDropDownIcon />
    </Button>
    <Popper open={!!anchorEl} anchorEl={anchorEl} style={{ marginTop: 5 }}>
      <Box style={{ padding: 0 }}>
        <ClickAwayListener onClickAway={() => handleClick(false)}>
          <MenuList disablePadding>
            {data.map((listItem: CampaignMenuItem, index: number) => {
              const { text, type } = listItem;

              const styleMenuItem: React.CSSProperties = {
                borderTop: index !== 0 ? `1px solid ${lightGray}` : '',
                justifyContent: 'center',
                minWidth: '200px',
              };

              const toSlug = `/campaigns/new?type=${type}`;

              return (
                <Link key={type} to={toSlug}>
                  <MenuItem style={styleMenuItem}>{text}</MenuItem>
                </Link>
              );
            })}
          </MenuList>
        </ClickAwayListener>
      </Box>
    </Popper>
  </>
);

const CampaignCreationButton = (): JSX.Element | null => {
  const toSlug = `/campaigns/new?type=${CampaignFormType.Campaign}`;

  return (
    <Link to={toSlug}>
      <Button color="primary" variant="outlined" size="large">
        Create Campaign
      </Button>
    </Link>
  );
};

const CampaignsPage: React.FC<{}> = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const params: Record<'type', 'active' | 'inactive' | 'shared'> = useParams();
  const campaigns: Map<string, Campaign> = useSelector(selectCampaigns);
  const campaignsToDisplay = campaigns.filter((item: [string, Campaign]) =>
    params.type === 'shared'
      ? item.isSharedWithSubsidiary && !item.optedIn
      : params.type === 'active'
      ? item.isActive && (!item.isSharedWithSubsidiary || item.optedIn)
      : !item.isActive && (!item.isSharedWithSubsidiary || item.optedIn)
  );

  if (user && (!user.hasPrivilege(Privileges.CAMPAIGN_MANAGE) || !organization?.isCompany())) {
    history.replace('/');
  }

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const selectCampaignsType = (type: string) => {
    history.replace(`/campaigns/${type}`);
  };

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    dispatch(initAction());
  }, []);

  const campaignsAllowedToBeCreated: CampaignMenuItem[] = [
    {
      text: 'Campaign',
      type: CampaignFormType.Campaign,
    },
  ];

  if (user?.hasFeature(Features.GIVING_PLEDGE_CAMPAIGNS)) {
    campaignsAllowedToBeCreated.push({
      text: 'Pledge Campaign',
      type: CampaignFormType.GivingPledge,
    });
  }

  const showCampaignCreationDropDown = campaignsAllowedToBeCreated.length > 1;

  return (
    <>
      <Helmet title="Campaigns" />
      <PageBannerContainer color="#E2F7FF" backgroundImage={CampaignsPageBanner}>
        <h1>Campaigns</h1>

        {showCampaignCreationDropDown ? (
          <CampaignCreationDropDown
            anchorEl={anchorEl}
            data={campaignsAllowedToBeCreated}
            handleClick={handleClick}
          ></CampaignCreationDropDown>
        ) : (
          <CampaignCreationButton></CampaignCreationButton>
        )}
      </PageBannerContainer>

      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <TabItem
              isActive={params.type === 'active'}
              onClick={(): void => {
                selectCampaignsType('active');
              }}
              label="Active"
            />
            <TabItem
              isActive={params.type === 'inactive'}
              onClick={(): void => {
                selectCampaignsType('inactive');
              }}
              label="Inactive"
            />
            {user?.organization?.parent && (
              <TabItem
                isActive={params.type === 'shared'}
                onClick={(): void => {
                  selectCampaignsType('shared');
                }}
                label="Shared"
              />
            )}
          </div>

          {campaigns && <CampaignsTable campaigns={campaignsToDisplay} tab={params.type} />}
        </>
      )}
    </>
  );
};

export default CampaignsPage;
